import { Controller } from "@hotwired/stimulus"
import { Spring, prefersReducedMotion } from "../lib/spring"

// The menu grows out of the control that opened it and shrinks back into it, so
// the relationship between the two is never in doubt. It can be closed while it
// is still opening — the spring takes over from wherever it currently is.
export default class extends Controller {
  static targets = ["menu"]
  static values = {
    open: { type: Boolean, default: false },
    origin: { type: String, default: "top right" }
  }

  connect() {
    this.boundCloseOnClickOutside = (e) => this.closeOnClickOutside(e)
    this.boundCloseOnEscape = (e) => this.closeOnEscape(e)
    window.addEventListener("click", this.boundCloseOnClickOutside)
    window.addEventListener("keydown", this.boundCloseOnEscape)

    this.menuTarget.style.transformOrigin = this.originValue
    this.menuTarget.style.willChange = "transform, opacity"
    this.menuTarget.setAttribute("role", "menu")

    // 0 is closed, 1 is open. Animating one number keeps scale and opacity
    // locked together however the target changes mid-flight.
    this.progress = new Spring({
      value: this.openValue ? 1 : 0,
      response: 0.28,
      damping: 1,
      onUpdate: (value) => this.draw(value),
      onRest: (value) => { if (value === 0) this.menuTarget.style.display = "none" }
    })

    this.openValueChanged(this.openValue)
  }

  disconnect() {
    window.removeEventListener("click", this.boundCloseOnClickOutside)
    window.removeEventListener("keydown", this.boundCloseOnEscape)
    if (this.progress) this.progress.stop()
  }

  get trigger() {
    return this.element.querySelector('[data-action*="dropdown#toggle"]')
  }

  toggle(event) {
    // A click carries pointer coordinates; a keyboard-triggered click does not.
    this.openedFromKeyboard = Boolean(event && event.detail === 0)
    this.openValue = !this.openValue
  }

  close() {
    this.openValue = false
  }

  openValueChanged(value) {
    if (!this.progress) return

    if (this.trigger) this.trigger.setAttribute("aria-expanded", String(value))

    if (value) {
      this.menuTarget.style.display = ""
      // Opening from the keyboard should land on the first item; opening by
      // pointer should not steal the caret away from where the user is looking.
      if (this.openedFromKeyboard) requestAnimationFrame(() => this.items[0]?.focus())
    } else if (this.menuTarget.contains(document.activeElement) && this.trigger) {
      this.trigger.focus()
    }

    if (prefersReducedMotion()) {
      this.progress.value = value ? 1 : 0
      this.draw(this.progress.value)
      if (!value) this.menuTarget.style.display = "none"
      return
    }

    this.progress.to(value ? 1 : 0)
  }

  draw(value) {
    const scale = 0.94 + (0.06 * value)
    this.menuTarget.style.transform = `scale(${scale})`
    this.menuTarget.style.opacity = String(value)
  }

  closeOnClickOutside(event) {
    if (this.openValue && !this.element.contains(event.target)) this.close()
  }

  // Escape closes the menu and hands focus back to what opened it, so keyboard
  // users don't end up stranded at the top of the document.
  closeOnEscape(event) {
    if (!this.openValue) return

    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      if (this.trigger) this.trigger.focus()
      return
    }

    this.navigate(event)
  }

  // Everything in the menu that can be reached with a keyboard, in the order it
  // appears on screen.
  get items() {
    return [ ...this.menuTarget.querySelectorAll("a[href], button:not(:disabled)") ]
      .filter((node) => node.offsetParent !== null)
  }

  // An open menu owns the arrow keys and the tab order. Tab escaping to the
  // page behind it leaves a keyboard user driving something they can't see.
  navigate(event) {
    const items = this.items
    if (items.length === 0) return

    const at = items.indexOf(document.activeElement)

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const step = event.key === "ArrowDown" ? 1 : -1
      const next = at === -1 ? (step === 1 ? 0 : items.length - 1) : (at + step + items.length) % items.length
      items[next].focus()
      return
    }

    if (event.key === "Home") {
      event.preventDefault()
      items[0].focus()
      return
    }

    if (event.key === "End") {
      event.preventDefault()
      items[items.length - 1].focus()
      return
    }

    if (event.key === "Tab") {
      event.preventDefault()
      const step = event.shiftKey ? -1 : 1
      const next = at === -1 ? 0 : (at + step + items.length) % items.length
      items[next].focus()
    }
  }
}
